import {
  type ComparisonPeriodType,
  type DatePeriodIntervalType,
  isNielsenInterval,
  type KpiPeriodFilterType,
  type KpiQueryFiltersType,
  type KpiQueryType,
  LIKE_FOR_LIKE_EMPTY_PERIMETER_STORE_KEY,
  type LookerPeriodType,
  type NielsenPeriodIntervalType,
  PERIOD_TO_LOOKER_PERIOD,
  type ProductTaxonomyType,
  type StorePerimeterType,
  DetentionPeriodValueType
} from '@carrefour-gcs/shared';
import { type Ref, unref } from 'vue';

/**
 * Throws when the Looker period field and the period interval shape disagree: `period_code` only
 * pairs with a Nielsen interval, every other field only pairs with a date interval.
 */
export function assertPeriodMatchesReferential(
  periodField: LookerPeriodType,
  periodInterval: DatePeriodIntervalType['periodInterval'] | NielsenPeriodIntervalType['periodInterval']
): void {
  if (isNielsenInterval(periodInterval)) {
    if (periodField !== 'period_code') {
      throw new Error(`Nielsen period are only available for period_code field. Given is: ${periodField}`);
    }
    return;
  }

  if (periodField === 'period_code') {
    throw new Error(
      `period_code field is only available with Nielsen period interval. Given interval is: ${JSON.stringify(
        periodInterval,
        null,
        2
      )}`
    );
  }
}

export interface PrepareQueryInputArgsType {
  period: Ref<DatePeriodIntervalType | NielsenPeriodIntervalType>;
  comparisonPeriod?: Ref<ComparisonPeriodType>;
  detentionPeriod?: Ref<DetentionPeriodValueType> | DetentionPeriodValueType;
  filters: Ref<KpiQueryFiltersType['filters']>;
  isWithTax: Ref<KpiQueryFiltersType['isWithTax']>;
  taxonomy: Ref<ProductTaxonomyType>;
  storePerimeter: Ref<StorePerimeterType>;
  isMyDataIncluded: Ref<KpiQueryFiltersType['isMyDataIncluded']>;
  /** Front-resolved L4L perimeter (resolveLikeForLikeStoreKeys), injected as the store_key filter. */
  likeForLikeStoreKeys?: string[];
}

/**
 * Overrides store_key with the front-resolved L4L perimeter (the API uses it as-is).
 * An EMPTY resolved perimeter means no comparable store: the sentinel forces zero results.
 * An undefined perimeter (route failed) keeps the filters so the API resolves it itself.
 */
export function withLikeForLikeStoreKeys(
  filters: KpiQueryFiltersType['filters'],
  storePerimeter: StorePerimeterType,
  likeForLikeStoreKeys: string[] | undefined
): KpiQueryFiltersType['filters'] {
  if (storePerimeter !== 'likeForLike' || likeForLikeStoreKeys === undefined) {
    return filters;
  }

  return {
    ...filters,
    store_key: likeForLikeStoreKeys.length > 0 ? likeForLikeStoreKeys : [LIKE_FOR_LIKE_EMPTY_PERIMETER_STORE_KEY]
  };
}

export function prepareQueryInput({
  period,
  comparisonPeriod,
  detentionPeriod,
  filters,
  isWithTax,
  taxonomy,
  storePerimeter,
  isMyDataIncluded,
  likeForLikeStoreKeys
}: PrepareQueryInputArgsType): KpiQueryType {
  const unrefPeriod = unref(period);
  const unrefComparisonPeriod = unref(comparisonPeriod);
  const unrefDetentionPeriod = unref(detentionPeriod);
  const unrefFilters = unref(filters);
  const unrefIsWithTax = unref(isWithTax);
  const unrefTaxonomy = unref(taxonomy);
  const unrefStorePerimeter = unref(storePerimeter);
  const unrefIsMyDataIncluded = unref(isMyDataIncluded);

  const periodField = PERIOD_TO_LOOKER_PERIOD[unrefPeriod.period];
  assertPeriodMatchesReferential(periodField, unrefPeriod.periodInterval);

  const commonQueryInput = {
    comparisonPeriod: unrefComparisonPeriod,
    ...(unrefDetentionPeriod && { detentionPeriod: unrefDetentionPeriod }),
    query: {
      filters: withLikeForLikeStoreKeys(unrefFilters, unrefStorePerimeter, likeForLikeStoreKeys),
      isWithTax: unrefIsWithTax,
      isMyDataIncluded: unrefIsMyDataIncluded
    },
    taxonomy: unrefTaxonomy,
    storePerimeter: unrefStorePerimeter
  };

  return {
    // assertPeriodMatchesReferential just proved field and value agree; the discriminated union
    // needs a matching control-flow narrowing that a single shared assertion cannot provide.
    period: { field: periodField, value: unrefPeriod.periodInterval } as KpiPeriodFilterType,
    ...commonQueryInput
  };
}
